import React from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { useHapticFeedback } from '@blade/ui-kit'
import { ArrowDown2Icon } from '@blade/ui-kit'
import { CurrencyIcon, CurrencyPlaceholder } from '@root/entities/currency/ui'
import { mediaMinTablet } from '@root/shared/ui-kit'

import type { CurrencyModel } from '@root/entities/currency/model'
import type { CurrencyPlaceholderProps } from '@root/entities/currency/ui/currency-icon/CurrencyPlaceholder'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  asset: CurrencyModel | undefined
  placeholderColor?: CurrencyPlaceholderProps['color']
  onClick?: () => void
} & ClassNameProps

export const CurrencySwitchButton: React.FC<Props> = ({
  asset: currency,
  onClick,
  className,
  placeholderColor = 'blue',
}) => {
  const { t } = useTranslation()
  const haptics = useHapticFeedback()

  const renderContent = () => {
    if (!currency) {
      return (
        <>
          <CurrencyPlaceholder color={placeholderColor} />
          <Trigger>
            <Ticker>{t('currency.select_currency')}</Ticker>
            <ArrowDown2Icon size={14} />
          </Trigger>
        </>
      )
    }

    return (
      <>
        <CurrencyIcon shape="circle" currency={currency} size={24} />
        <CurrencyInfoContainer>
          <Trigger>
            <Ticker>{currency.ticker}</Ticker>
            <ArrowDown2Icon size={14} />
          </Trigger>
        </CurrencyInfoContainer>
      </>
    )
  }

  return (
    <Root
      className={className}
      onClick={() => {
        if (onClick) {
          haptics.impactOccurred('light')
          onClick()
        }
      }}
    >
      {renderContent()}
    </Root>
  )
}

const Root = styled.div`
  color: var(--text-12);
  background: var(--backgroundA-3);
  display: grid;
  align-items: center;
  grid-auto-flow: column;
  grid-template-columns: max-content auto;
  gap: 4px;

  border-radius: 30px;
  padding-inline: 2px 6px;
  padding-block: 2px;
  cursor: pointer;
  margin-bottom: 1px;
  transition: color 0.15s linear;

  ${mediaMinTablet(css`
    background: transparent;
    padding-inline: 0;
    &:hover {
      color: var(--text-11);
    }
  `)}
`

const Ticker = styled.div`
  font-weight: 600;
  font-size: 11px;
  white-space: nowrap;
`

const Trigger = styled.div`
  display: flex;
  align-items: center;
  height: 16px;
  overflow: hidden;
  gap: 3px;
`

const CurrencyInfoContainer = styled.div`
  display: grid;
  gap: 5px;
  position: relative;
`
